<script lang="ts">
import { onMount } from 'svelte';
import { ChatClient } from './lib/chatClient';
import {
  getSignature,
  storeSignature,
  requestSignature,
  getSignatureDisplay,
} from './lib/signatureStore';

import type { Chat, Message, Member, UserInfo, ServerMessage } from './lib/types';
import type { ChatClient as ChatClientType } from './lib/chatClient';

  import Auth from './routes/Auth.svelte';
  import ChatList from './routes/ChatList.svelte';
  import ChatMessages from './routes/ChatMessages.svelte';
  import ChatInfo from './routes/ChatInfo.svelte';
  import ChatMembers from './routes/ChatMembers.svelte';
  import SignaturePopup from './routes/SignaturePopup.svelte';
  import ChatInfoPopup from './routes/ChatInfoPopup.svelte';
  import HealthPopup from './routes/HealthPopup.svelte';

  const browser = typeof window !== 'undefined' && typeof window.document !== 'undefined';

  // ─── App state ────────────────────────────────────────────────────────────
  let authenticated = false;
  let loading = true;
  let loadingStatus = 'Connecting to server…';
  let connected = false;
  let client: ChatClientType;
  let error = '';

  let userInfo: UserInfo | null = null;
  let currentUserSignature: { name: string; avatar_url: string } | null = null;
  let chats: Chat[] = [];
  let messages: Message[] = [];
  let members: Member[] = [];
  let hasMoreHistory = true;
  let scrollBehavior: 'bottom' | 'bottom-if-near' | 'anchor' | 'none' = 'bottom';
  let chatMessagesRef: any;

  // Popup state
  let showSignaturePopup = false;
  let signatureLoading = false;
  let showChatInfoPopup = false;
  let showHealthPopup = false;

  // ─── Routing ──────────────────────────────────────────────────────────────
  let chatId: string | null = null;
  let currentChat: Chat | undefined;
  let view: 'list' | 'messages' = 'list';

  const CHAT_HISTORY_SEGMENT_LENGTH = 10;

  const updateCurrentChat = (): void => {
    currentChat = chatId ? chats.find((c) => c.uuid === chatId) : undefined;
  };

  const updateView = (): void => {
    view = chatId ? 'messages' : 'list';
  };

  function readHash(): string | null {
    if (!browser) return null;
    return window.location.hash.slice(1) || null;
  }

  function handleHashChange(): void {
    const newChatId = readHash();
    if (newChatId !== chatId) {
      messages = [];
      members = [];
      hasMoreHistory = true;
      scrollBehavior = 'bottom';
      chatId = newChatId;
      updateView();
      updateCurrentChat();
      if (newChatId) {
        const idx = chats.findIndex((c) => c.uuid === newChatId);
        if (idx !== -1 && chats[idx].unread_count > 0) {
          chats[idx] = { ...chats[idx], unread_count: 0 };
          chats = [...chats];
        }
      }
    }
  }

  onMount(() => {
    if (!browser) return;
    window.addEventListener('hashchange', handleHashChange);

    chatId = readHash();
    updateView();
    updateCurrentChat();

    client = new ChatClient();
    client.init();

    const sessionUuid = localStorage.getItem('session_uuid');

    (async () => {
      if (sessionUuid) {
        loadingStatus = 'Reconnecting…';
        reconnect();
      } else {
        loadingStatus = 'Connecting to server…';
        await client.waitForBackend();
        loading = false;
      }
    })();

    client.onConnection((isConnected) => {
      console.log('[APP] Connection:', isConnected);
      connected = isConnected;
      if (isConnected) {
        authenticated = true;
        loading = false;
        loadInitialData();
      }
    });

    client.onMessage(handleMessage);

    return () => {
      client.disconnect();
      window.removeEventListener('hashchange', handleHashChange);
    };
  });

  const reconnect = (): void => {
    const userUuid = localStorage.getItem('user_uuid');
    const sessionUuid = localStorage.getItem('session_uuid');

    if (!userUuid || !sessionUuid) {
      logout();
      loading = false;
      return;
    }

    userInfo = { uuid: userUuid };
    console.log('[APP] Reconnecting with session', sessionUuid.slice(0, 8) + '…');
    client.connect();
  };

  const handleAuth = async (event: CustomEvent): Promise<void> => {
    loading = true;
    loadingStatus = 'Authenticating…';
    error = '';

    try {
      await client.login(event.detail);
      const userUuid = localStorage.getItem('user_uuid');
      if (userUuid) userInfo = { uuid: userUuid };
      client.connect();
    } catch {
      error = 'Authentication failed';
      loading = false;
    }
  };

  const loadInitialData = (): void => {
    console.log('[APP] Loading initial data');
    client.getUserInfo();
    client.getChatList();
    if (chatId) loadChatData();
  };

  const loadChatData = (): void => {
    if (!client || !chatId) return;
    if (view === 'messages') {
      if (messages.length === 0) client.getChatHistory(chatId);
      client.getChatMembers(chatId);
    }
  };

  const handleMessage = (msg: ServerMessage): void => {
    console.log('[APP] ←', msg.type);
    switch (msg.type) {
      case 'SrvResChatList':
        chats = msg.uuids.map((uuid: string) => ({
          uuid,
          name: `Chat ${uuid.slice(0, 8)}`,
          avatar_url: '/favicon.png',
          signature_uuid: '',
          unread_count: 0,
          last_activity: undefined,
          can_write: true,
        }));
        chats.forEach((chat) => client.getChatInfo(chat.uuid));
        updateCurrentChat();
        break;

      case 'SrvResChatInfo': {
        const chatInfo = msg.chat;
        chats = chats.map((chat) =>
          chat.uuid === chatInfo.uuid
            ? { ...chat, signature_uuid: chatInfo.signature_uuid, can_write: true }
            : chat,
        );
        if (chatInfo.signature_uuid) {
          requestSignature(chatInfo.signature_uuid, (uuid) =>
            client.getSignatureInfo(uuid),
          );
        }
        updateCurrentChat();
        break;
      }

      case 'SrvResChatHistorySegment':
        if (msg.chat_uuid === chatId) {
          hasMoreHistory = msg.has_more;

          const existingUuids = new Set(messages.map((m) => m.uuid));
          const fresh = msg.messages.filter((m: any) => !existingUuids.has(m.uuid));

          if (fresh.length === 0) break;

          const formatted = fresh.map((m: any) => {
            if (m.signature_uuid) {
              requestSignature(m.signature_uuid, (uuid) =>
                client.getSignatureInfo(uuid),
              );
            }
            const display = m.signature_uuid
              ? getSignatureDisplay(m.signature_uuid)
              : { name: `User ${m.user_uuid.slice(0, 8)}`, avatar_url: '/favicon.png' };

            return {
              id: m.uuid,
              uuid: m.uuid,
              content: m.content,
              username: display.name,
              avatar_url: display.avatar_url,
              timestamp: new Date(m.created_at).toLocaleTimeString(),
              created_at: m.created_at,
              user_uuid: m.user_uuid,
              signature_uuid: m.signature_uuid,
              is_own: m.user_uuid === userInfo?.uuid,
            };
          });

          scrollBehavior = messages.length === 0 ? 'bottom' : 'anchor';
          messages = [...formatted, ...messages];
          addDateLabels(messages);
        }
        break;

      case 'SrvMessage':
        if (msg.chat_uuid === chatId) {
          if (msg.signature_uuid) {
            requestSignature(msg.signature_uuid, (uuid) =>
              client.getSignatureInfo(uuid),
            );
          }
          const display = msg.signature_uuid
            ? getSignatureDisplay(msg.signature_uuid)
            : { name: `User ${msg.user_uuid.slice(0, 8)}`, avatar_url: '/favicon.png' };

          const liveId = `live_${msg.user_uuid}_${msg.created_at}`;
          scrollBehavior = 'bottom-if-near';
          messages = [
            ...messages,
            {
              id: liveId,
              uuid: liveId,
              content: msg.content,
              username: display.name,
              avatar_url: display.avatar_url,
              timestamp: new Date(msg.created_at).toLocaleTimeString(),
              created_at: msg.created_at,
              user_uuid: msg.user_uuid,
              signature_uuid: msg.signature_uuid,
              is_own: msg.user_uuid === userInfo?.uuid,
            },
          ];
          addDateLabels(messages);
        }
        if (msg.chat_uuid !== chatId) {
          chats = chats.map((chat) =>
            chat.uuid === msg.chat_uuid
              ? { ...chat, unread_count: chat.unread_count + 1, last_activity: msg.created_at }
              : chat,
          );
        }
        break;

      case 'SrvResChatMembers':
        if (msg.chat_uuid === chatId) {
          members = msg.uuids.map((uuid: string) => {
            client.getUserInfo(uuid);
            return {
              uuid,
              username: `User ${uuid.slice(0, 8)}`,
              avatar_url: '/favicon.png',
              is_online: false,
              last_active_at: undefined,
              signature_uuid: undefined,
            };
          });
        }
        break;

      case 'SrvResSignatureInfo':
        storeSignature(msg.signature);
        chats = chats.map((chat) =>
          chat.signature_uuid === msg.signature.uuid
            ? { ...chat, name: msg.signature.name, avatar_url: msg.signature.avatar_url }
            : chat,
        );
        updateCurrentChat();

        if (userInfo?.signature_uuid === msg.signature.uuid) {
          currentUserSignature = {
            name: msg.signature.name,
            avatar_url: msg.signature.avatar_url,
          };
        }
        messages = messages.map((m) =>
          m.signature_uuid === msg.signature.uuid
            ? { ...m, username: msg.signature.name, avatar_url: msg.signature.avatar_url }
            : m,
        );
        members = members.map((m) =>
          m.signature_uuid === msg.signature.uuid
            ? { ...m, username: msg.signature.name, avatar_url: msg.signature.avatar_url }
            : m,
        );
        break;

      case 'SrvResUserInfo':
        if (msg.user_uuid === userInfo?.uuid) {
          userInfo = {
            uuid: msg.user_uuid,
            signature_uuid: msg.signature_uuid,
            created_at: msg.last_active_at,
            provider: msg.provider,
          };
          if (msg.signature_uuid) {
            requestSignature(msg.signature_uuid, (uuid) =>
              client.getSignatureInfo(uuid),
            );
          } else {
            currentUserSignature = {
              name: msg.provider === 'anonymous' ? 'Anonymous' : `User ${msg.user_uuid.slice(0, 8)}`,
              avatar_url: '/favicon.png',
            };
          }
        } else {
          members = members.map((member) =>
            member.uuid === msg.user_uuid
              ? {
                  ...member,
                  signature_uuid: msg.signature_uuid,
                  last_active_at: msg.last_active_at,
                  username: msg.signature_uuid
                    ? getSignatureDisplay(msg.signature_uuid).name
                    : `User ${msg.user_uuid.slice(0, 8)}`,
                  avatar_url: msg.signature_uuid
                    ? getSignatureDisplay(msg.signature_uuid).avatar_url
                    : '/favicon.png',
                }
              : member,
          );
          if (msg.signature_uuid) {
            requestSignature(msg.signature_uuid, (uuid) =>
              client.getSignatureInfo(uuid),
            );
          }
        }
        break;

      case 'SrvResNewSignature': {
        const { signature } = msg;
        storeSignature(signature);
        if (userInfo) {
          userInfo = { ...userInfo, signature_uuid: signature.uuid };
          currentUserSignature = { name: signature.name, avatar_url: signature.avatar_url };
        }
        showSignaturePopup = false;
        signatureLoading = false;
        error = '';
        break;
      }

      case 'SrvSetUserStatusChanged':
        members = members.map((m) =>
          m.uuid === msg.user_uuid ? { ...m, is_online: msg.online } : m,
        );
        break;

      case 'error':
        console.error('[APP] Server error:', msg.message, msg.code);
        if (
          msg.message === 'Invalid session token' ||
          msg.message === 'Invalid session' ||
          msg.message === 'Session expired' ||
          msg.code === 'INVALID_SESSION' ||
          msg.code === 'SESSION_EXPIRED'
        ) {
          logout();
          return;
        }
        error = msg.message || 'An error occurred';
        if (signatureLoading) {
          signatureLoading = false;
          showSignaturePopup = false;
        }
        break;

      default:
        break;
    }
  };

  const addDateLabels = (list: Message[]): void => {
    list.sort((a, b) => {
      const da = new Date(a.created_at ?? '').getTime();
      const db = new Date(b.created_at ?? '').getTime();
      return da - db;
    });
    let lastDate = '';
    for (const msg of list) {
      if (!msg.created_at) { delete msg.date_label; continue; }
      const dateStr = new Date(msg.created_at).toDateString();
      if (dateStr !== lastDate) {
        msg.date_label = msg.created_at;
        lastDate = dateStr;
      } else {
        delete msg.date_label;
      }
    }
  };

  const sendMessage = (event: CustomEvent): void => {
    if (chatId) client.sendMessage(chatId, event.detail);
  };

  const loadHistory = (event: CustomEvent): void => {
    if (!chatId || !hasMoreHistory) return;
    const oldest = event.detail as Message;
    const before = oldest?.created_at ? new Date(oldest.created_at) : undefined;
    client.getChatHistory(chatId, before);
  };

  const logout = (): void => {
    client?.disconnect();
    localStorage.removeItem('session_uuid');
    localStorage.removeItem('user_uuid');
    localStorage.removeItem('session_expires_at');
    authenticated = false;
    connected = false;
    userInfo = null;
    currentUserSignature = null;
    chats = [];
    messages = [];
    members = [];
    chatId = null;
    currentChat = undefined;
    view = 'list';
    error = '';
    hasMoreHistory = true;
    loading = false;
  };

  const openSignaturePopup = (): void => {
    if (userInfo?.provider === 'anonymous') return;
    showSignaturePopup = true;
  };

  const handleSignatureSubmit = (event: CustomEvent): void => {
    const { name, avatar_url } = event.detail;
    signatureLoading = true;
    error = '';
    try {
      client.createSignature(name, avatar_url);
    } catch (err) {
      error = err instanceof Error ? err.message : 'Failed to create signature';
      signatureLoading = false;
      showSignaturePopup = false;
    }
  };

  $: if (view && chatId && client && authenticated) {
    if (view === 'messages' && messages.length === 0) loadChatData();
  }
</script>

{#if loading}
  <div class="loading">
    <div class="spinner" aria-hidden="true"></div>
    <p>{loadingStatus}</p>
  </div>
{:else if !authenticated}
  <Auth on:auth={handleAuth} />
{:else}
  <header class="app-header">
    <div class="header-fixed-left">
      <button class="status-btn" class:connected on:click={() => (showHealthPopup = true)}>
        ●
      </button>
      <div class="pipe">|</div>
    </div>

    <div class="header-content">
      {#if view === 'list'}
        <a href="/" class="pill user-link">
          <img
            src={currentUserSignature?.avatar_url || '/favicon.png'}
            alt={currentUserSignature?.name || 'User'}
          />
          <span>#{
              currentUserSignature?.name || (userInfo?.provider === 'anonymous' ? 'Anonymous' : 'User')
            }</span>
        </a>
        {#if userInfo?.provider !== 'anonymous'}
          <button class="pill signature-pill" on:click={openSignaturePopup}>Edit Profile</button>
        {/if}
      {:else if currentChat}
        <a href="/" class="pill user-link short">
          <img
            src={currentUserSignature?.avatar_url || '/favicon.png'}
            alt={currentUserSignature?.name || 'User'}
          />
          <span>#{
              currentUserSignature?.name || (userInfo?.provider === 'anonymous' ? 'Anonymous' : 'User')
            }</span>
        </a>
        <button class="pill chat-pill" on:click={() => (showChatInfoPopup = true)}>
          <img src={currentChat.avatar_url} alt={currentChat.name} />
          <span>{currentChat.name}</span>
        </button>
      {/if}
    </div>

    <div class="header-fixed-right">
      <div class="pipe">|</div>
      <button class="logout-btn" on:click={logout}>×</button>
    </div>
  </header>

  {#if error}
    <div class="error">{error}</div>
  {/if}

  <main class="app-main">
    {#if view === 'list'}
      <ChatList {chats} />
    {:else if view === 'messages' && currentChat}
      <ChatMessages
        bind:this={chatMessagesRef}
        chat={currentChat}
        {messages}
        {hasMoreHistory}
        {scrollBehavior}
        on:send={sendMessage}
        on:loadHistory={loadHistory}
        on:scrollConsumed={() => (scrollBehavior = 'none')}
      />
    {:else if view === 'messages' && chats.length === 0}
      <div class="loading">
        <div class="spinner"></div>
        <p>Loading chat…</p>
      </div>
    {:else if view === 'messages'}
      <div class="error">Chat not found</div>
    {/if}
  </main>

  {#if userInfo?.provider !== 'anonymous'}
    <SignaturePopup
      isOpen={showSignaturePopup}
      currentName={currentUserSignature?.name || `User ${userInfo?.uuid?.slice(0, 8) || ''}`}
      currentAvatarUrl={currentUserSignature?.avatar_url || '/favicon.png'}
      loading={signatureLoading}
      on:close={() => { if (!signatureLoading) showSignaturePopup = false; }}
      on:submit={handleSignatureSubmit}
    />
  {/if}

  {#if currentChat}
    <ChatInfoPopup
      isOpen={showChatInfoPopup}
      chat={currentChat}
      {members}
      on:close={() => (showChatInfoPopup = false)}
    />
  {/if}

  <HealthPopup
    isOpen={showHealthPopup}
    {connected}
    on:close={() => (showHealthPopup = false)}
  />
{/if}

<style>
/* ── Loading screen ─────────────────────────────────────────────────────── */
.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  gap: 1rem;
  font-size: 1rem;
  color: #555;
}

.spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #e0e0e0;
  border-top-color: #007bff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ── Error banner ───────────────────────────────────────────────────────── */
.error {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: #f8d7da;
  color: #dc3545;
  text-align: center;
  font-size: 1rem;
}

/* ── Header ─────────────────────────────────────────────────────────────── */
.app-header {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  background: white;
  border-bottom: 1px solid #eee;
  gap: 0.5rem;
}

.header-fixed-left,
.header-fixed-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.status-btn {
  background: none;
  border: none;
  color: #dc3545;
  font-size: 1rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 50%;
}

.status-btn.connected { color: #28a745; }
.status-btn:hover { background: rgba(0, 0, 0, 0.05); }

.pipe { color: #ddd; font-size: 1rem; user-select: none; }

.header-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  overflow-x: auto;
  min-width: 0;
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.header-content::-webkit-scrollbar { display: none; }

/* ── Pills ──────────────────────────────────────────────────────────────── */
.pill {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: white;
  border: 1px solid #ddd;
  padding: 0.35rem 0.6rem;
  border-radius: 20px;
  cursor: pointer;
  font-size: 0.85rem;
  white-space: nowrap;
  transition: all 0.2s;
  text-decoration: none;
  color: inherit;
}
.pill:hover:not(:disabled) { background: #f8f9fa; border-color: #bbb; }
.pill img { width: 18px; height: 18px; border-radius: 50%; }
.pill.short span { display: none; }

.user-link { border-color: #6c757d; color: #6c757d; }
.user-link:hover { background: #f8f9fa; text-decoration: none; color: #6c757d; }

.signature-pill { border-color: #17a2b8; color: #17a2b8; }
.signature-pill:hover { background: #e6f7ff; }

.chat-pill { border-color: #007bff; color: #007bff; }
.chat-pill:hover { background: #e7f3ff; }

/* ── Logout ─────────────────────────────────────────────────────────────── */
.logout-btn {
  background: #dc3545;
  color: white;
  border: none;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.logout-btn:hover { background: #c82333; }

/* ── Main ───────────────────────────────────────────────────────────────── */
.app-main { height: calc(100vh - 80px); }

:global(body) {
  margin: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}
:global(*) { box-sizing: border-box; }
</style>
